import React from "react";
import { Route } from "react-router-dom";
import type { RouteCtx } from "../routeContext";
import { PlaceholderPage } from "../shell";
import {
  StudyManagement,
  UserManagement,
  SiteManagement,
  MonitoringTracker,
  CalendarPage,
  StudyDashboard,
  SiteDashboard,
  RegulatoryApprovalsMain,
  ApprovalDetailPage,
  ActivityHistory,
  CRFDesigner,
  CRFTemplates,
  SubjectsManagement,
  VisitDataEntry,
  EDCDataReviewPage,
  DataCompletenessDashboard,
  ExportsEDC,
  DocumentLibrary,
  UploadDocument,
  QCReview,
  ETMFCompletenessDashboard,
  RetentionArchival,
  InspectionMode,
  ModelGovernance,
  MigrationReview,
  CDMSDataReviewPage,
  QueryManagementPage,
  SDVSDRPage,
  CodingPage,
  DataLocksPage,
  DataExtractsPage,
  CDMSDashboardsPage,
  ProfilePage,
  AuditLogsRoute,
  FinancePayments,
  IssueRiskTrackerPage,
  QMSPage,
} from "../lazyPages";

/** consolidatedRoutes — order preserved for React Router first-match. */
export function consolidatedRoutes(ctx: RouteCtx): React.ReactNode {
  const { user, userRole, selectedStudyId, availableStudies, currentStudy } =
    ctx;

  return (
    <>
      {/* ── 5. CONSOLIDATED catalog (UNGATED — same component for all roles) */}
      {/* Prefer editing these for shared paths. First match wins if duplicated above. */}
      {/* Study Dashboard - used by multiple paths */}
      {[
        "/study-dashboard",
        "/ctms/study-dashboard",
        "/tenant/study-dashboard",
        "/pi/study-dashboard",
        "/sc/study-dashboard",
        "/cra/study-dashboard",
        "/dm/study-dashboard",
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={<StudyDashboard currentStudy={currentStudy} />}
        />
      ))}

      {/* Site Dashboard - used by multiple paths */}
      {[
        "/site-dashboard",
        "/ctms/site-dashboard",
        "/tenant/site-dashboard",
        "/cra/site-dashboard",
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={<SiteDashboard currentStudy={currentStudy} />}
        />
      ))}

      {/* Activity History - used by multiple paths */}
      <Route path="/activity-history" element={<ActivityHistory />} />
      <Route path="/tenant/activity-history" element={<ActivityHistory />} />
      <Route path="/ctms/activity-history" element={<ActivityHistory />} />

      {/* User Management */}
      <Route path="/users" element={<UserManagement />} />
      <Route path="/ctms/users" element={<UserManagement />} />
      <Route path="/tenant/users" element={<UserManagement />} />

      {/* Study Management */}
      <Route
        path="/studies"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/ctms/studies"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/tenant/studies"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/pi/studies"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/sc/studies"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />

      {/* Site Management */}
      <Route
        path="/sites"
        element={
          <SiteManagement
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            availableStudies={availableStudies}
          />
        }
      />
      <Route
        path="/ctms/sites"
        element={
          <SiteManagement
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            availableStudies={availableStudies}
          />
        }
      />
      <Route
        path="/tenant/sites"
        element={
          <SiteManagement
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            availableStudies={availableStudies}
          />
        }
      />

      {/* Monitoring Tracker */}
      <Route
        path="/monitor-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/monitoring-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/ctms/monitoring-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/tenant/monitoring-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/cra/monitoring-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/cra/monitor-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/pm/monitor-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />

      {/* Regulatory - consolidated for all roles */}
      <Route
        path="/regulatory"
        element={
          <RegulatoryApprovalsMain
            currentStudy={currentStudy}
            userRole={userRole}
          />
        }
      />
      <Route path="/regulatory/:studyId/:id" element={<ApprovalDetailPage />} />
      <Route
        path="/ctms/regulatory"
        element={
          <RegulatoryApprovalsMain
            currentStudy={currentStudy}
            userRole={userRole}
          />
        }
      />
      <Route
        path="/ctms/regulatory/:studyId/:id"
        element={<ApprovalDetailPage />}
      />
      <Route
        path="/tenant/regulatory"
        element={
          <RegulatoryApprovalsMain
            currentStudy={currentStudy}
            userRole={userRole}
          />
        }
      />
      <Route
        path="/tenant/regulatory/:studyId/:id"
        element={<ApprovalDetailPage />}
      />
      <Route
        path="/cra/regulatory"
        element={
          <RegulatoryApprovalsMain
            currentStudy={currentStudy}
            userRole={userRole}
          />
        }
      />
      <Route
        path="/cra/regulatory/:studyId/:id"
        element={<ApprovalDetailPage />}
      />

      {/* IWRS Routes - shared across roles */}
      <Route
        path="/iwrs/randomization"
        element={<PlaceholderPage title="Randomization" />}
      />
      <Route
        path="/iwrs/inventory"
        element={<PlaceholderPage title="Inventory & Kits" />}
      />
      <Route
        path="/iwrs/kit-assignment"
        element={<PlaceholderPage title="Kit Assignment" />}
      />
      <Route
        path="/iwrs/unblinding"
        element={<PlaceholderPage title="Unblinding" />}
      />
      <Route
        path="/iwrs/supply-management"
        element={<PlaceholderPage title="Supply Management" />}
      />
      <Route
        path="/iwrs/dashboards"
        element={<PlaceholderPage title="IWRS Dashboards" />}
      />

      {/* Profile - accessible to all roles */}
      <Route path="/profile" element={<ProfilePage />} />

      {/* Calendar */}
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/sc/calendar" element={<CalendarPage />} />

      {/* EDC Routes - consolidated for all roles */}
      <Route path="/edc/crf-designer" element={<CRFDesigner />} />
      <Route path="/edc/crf-templates" element={<CRFTemplates />} />
      <Route path="/edc/subjects" element={<SubjectsManagement />} />
      <Route
        path="/edc/visit-data-entry"
        element={<VisitDataEntry userRole={userRole} />}
      />
      <Route path="/edc/data-review" element={<EDCDataReviewPage />} />
      <Route
        path="/edc/data-completeness"
        element={<DataCompletenessDashboard />}
      />
      <Route path="/edc/exports" element={<ExportsEDC />} />

      {/* eTMF Routes - consolidated for all roles */}
      <Route
        path="/etmf/document-library"
        element={
          <DocumentLibrary
            userRole={userRole}
            selectedStudyId={selectedStudyId}
          />
        }
      />
      <Route
        path="/etmf/upload-document"
        element={
          <UploadDocument
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/qc-review"
        element={
          <QCReview userRole={userRole} selectedStudyId={selectedStudyId} />
        }
      />
      <Route
        path="/etmf/completeness-dashboard"
        element={
          <ETMFCompletenessDashboard selectedStudyId={selectedStudyId} />
        }
      />
      <Route path="/etmf/retention-archival" element={<RetentionArchival />} />
      <Route
        path="/etmf/inspection-mode"
        element={
          <InspectionMode
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/model-governance"
        element={
          <ModelGovernance
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/migration-review"
        element={
          <MigrationReview
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />

      {/* CDMS Routes - consolidated for all roles */}
      <Route path="/cdms/data-review" element={<CDMSDataReviewPage />} />
      <Route path="/cdms/query-management" element={<QueryManagementPage />} />
      <Route path="/cdms/sdv-sdr" element={<SDVSDRPage />} />
      <Route path="/cdms/coding" element={<CodingPage />} />
      <Route path="/cdms/data-locks" element={<DataLocksPage />} />
      <Route path="/cdms/data-extracts" element={<DataExtractsPage />} />
      <Route path="/cdms/dashboards" element={<CDMSDashboardsPage />} />
      <Route path="/audit-logs" element={<AuditLogsRoute user={user} />} />
      <Route
        path="/admin/audit-logs"
        element={<AuditLogsRoute user={user} />}
      />
      <Route
        path="/ctms/finance"
        element={
          <FinancePayments userRole={userRole} currentStudy={currentStudy} />
        }
      />

      {/* QMS — available to all roles that pass QMS_RBAC.hasAccess (page enforces) */}
      <Route
        path="/ctms/quality-risk"
        element={<QMSPage userRole={userRole} currentStudy={currentStudy} />}
      />

      {/* Issue & Risk Tracker - consolidated aliases (role sections may also register) */}
      {[
        "/risk-tracker",
        "/sponsor/risk-tracker",
        "/cra/risk-tracker",
        "/sc/risk-tracker",
        "/pm/risk-tracker",
        "/dm/risk-tracker",
        "/pi/risk-tracker",
        "/ctms/risk-tracker",
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <IssueRiskTrackerPage
              userRole={userRole}
              currentStudy={currentStudy}
            />
          }
        />
      ))}
    </>
  );
}
